import { useState } from "react";
import { useTranslation } from "react-i18next";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import Restricted from "@/consumer/restricted";
import RestrictedPage from "@/consumer/restrictedPage";
import { Tooltip, Link } from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import LanguageIcon from "@mui/icons-material/Language";
import { useNavigate } from "react-router-dom";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import YouTubePlayer from "@/components/general/youTubePlayer";
import styles from "@/routes/admin/examinationList.module.css";
import { replaceDate } from "@/helper/dateHandling";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { examinationEntityAdapter } from "@/routes/admin/examinationEntityAdapter";

export default function ExaminationList() {
  const { t } = useTranslation();
  usePageHeading(t("examinations"));
  const navigate = useNavigate();
  const { handleError, errorModal } = useErrorModal();
  const [youTubePlayerUrl, setYouTubePlayerUrl] = useState<string>("");
  const [youTubePlayerOpen, setYouTubePlayerOpen] = useState<boolean>(false);

  const navigateToFormTranslationEdit = (row: any) => {
    navigate(`/examination/${row.original.id}/translation`);
  };

  const handleExaminationTrackedServiceTemplates = async (
    examinationData: any,
    formValues: any = {},
    isEdit = false
  ) => {
    const newTemplateIds =
      formValues?.examination_tracked_service_template_set ?? [];
    const examinationId = examinationData.id;
    const promises = [];

    if (isEdit) {
      // # edit
      const originalTemplateAssociations =
        examinationData.examination_tracked_service_template_set ?? [];
      const originalTemplateIds = originalTemplateAssociations.map(
        (association: any) => association.tracked_service_template.id
      );

      const templatesToAdd = newTemplateIds.filter(
        (id: any) => !originalTemplateIds.includes(id)
      );
      const templatesToRemove = originalTemplateIds.filter(
        (id: any) => !newTemplateIds.includes(id)
      );

      // # add
      for (const templateId of templatesToAdd) {
        promises.push(
          new Promise<void>((resolve, reject) => {
            api.genericApiRequest({
              method: "post",
              entity: "examinationTrackedServiceTemplate",
              successHandler: () => resolve(),
              failHandler: (error: any) => reject(error),
              submitData: {
                examination: examinationId,
                tracked_service_template: templateId,
                conditions: [],
              },
              config: {
                headers: { "Content-Type": "application/json" },
              },
            });
          })
        );
      }

      // # remove
      for (const templateId of templatesToRemove) {
        const associationToDelete = originalTemplateAssociations.find(
          (association: any) =>
            association.tracked_service_template.id === templateId
        );

        if (associationToDelete) {
          promises.push(
            new Promise<void>((resolve, reject) => {
              api.genericApiRequest({
                method: "delete",
                entity: "examinationTrackedServiceTemplate",
                successHandler: () => resolve(),
                failHandler: (error: any) => reject(error),
                entityId: associationToDelete.id,
                config: {
                  headers: { "Content-Type": "application/json" },
                },
              });
            })
          );
        }
      }
    } else {
      // # create
      for (const templateId of newTemplateIds) {
        promises.push(
          new Promise<void>((resolve, reject) => {
            api.genericApiRequest({
              method: "post",
              entity: "examinationTrackedServiceTemplate",
              successHandler: () => resolve(),
              failHandler: (error: any) => reject(error),
              submitData: {
                examination: examinationId,
                tracked_service_template: templateId,
                conditions: [],
              },
              config: {
                headers: { "Content-Type": "application/json" },
              },
            });
          })
        );
      }
    }

    try {
      if (promises.length > 0) {
        await Promise.all(promises);
      }
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleExaminationTrackedServiceTemplatesDuringCreate = async (
    examinationData: any,
    formValues: any
  ) => {
    return handleExaminationTrackedServiceTemplates(
      examinationData,
      formValues,
      false
    );
  };

  const handleExaminationTrackedServiceTemplatesEdit = async (
    examinationData: any,
    formValues: any
  ) => {
    return handleExaminationTrackedServiceTemplates(
      examinationData,
      formValues,
      true
    );
  };

  const customRowActions = (row: any) => {
    return (
      <>
        <Restricted permissionRequired="frontend_view_entity_translation">
          <Tooltip
            arrow
            placement="top"
            enterDelay={1000}
            enterNextDelay={250}
            title={t("translation_edit")}
          >
            <GenericButton
              variant="icon"
              onClick={() => navigateToFormTranslationEdit(row)}
            >
              <LanguageIcon />
            </GenericButton>
          </Tooltip>
        </Restricted>
      </>
    );
  };

  return (
    <RestrictedPage permissionRequired="frontend_view_examination">
      <GenericCrudTable
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        customRowActions={customRowActions}
        additionalUrlParameter={{ include_relations: true }}
        entity={"examination"}
        entityAdapter={examinationEntityAdapter}
        additionalOnSuccessfulCreateFunction={
          handleExaminationTrackedServiceTemplatesDuringCreate
        }
        additionalOnSuccessfulEditFunction={
          handleExaminationTrackedServiceTemplatesEdit
        }
        columns={[
          { header: t("id"), accessorKey: "id", id: "id" },
          {
            header: t("name"),
            accessorKey: "name",
            id: "name",
            enableForCreate: true,
            required: true,
            errorMessage: t("field_required"),
          },
          {
            header: t("description"),
            accessorKey: "description",
            id: "description",
            enableForCreate: true,
            textarea: true,
          },
          {
            header: t("youtube_video_url"),
            accessorKey: "youtube_video_url",
            id: "youtube_video_url",
            enableForCreate: true,
            accessorFn: (row: any) => {
              const youtubeVideoUrl = row.youtube_video_url;
              return (
                <span>
                  {youtubeVideoUrl && (
                    <Link
                      onClick={() => {
                        setYouTubePlayerUrl(youtubeVideoUrl);
                        setYouTubePlayerOpen(true);
                      }}
                      className={styles.youtubeVideoUrl}
                    >
                      {youtubeVideoUrl}
                    </Link>
                  )}
                </span>
              );
            },
          },
          {
            header: t("external_name"),
            accessorKey: "external_title",
            id: "external_title",
            enableForCreate: true,
          },
          {
            header: t("category_set"),
            accessorKey: "category_set",
            id: "category_set",
            customColumnFilterId: "category_set__name",
            required: true,
            errorMessage: t("field_required"),
            accessorFn: (row: any) => {
              const categoryList = [];
              if (row.category_set) {
                for (const key in row.category_set) {
                  if (row.category_set[key].name) {
                    categoryList.push(row.category_set[key].name);
                  }
                }
              }

              return categoryList.join(", ");
            },
            enableForCreate: true,
            multiselect: true,
            entity: "mainCategory",
          },
          {
            header: t("linked_trackedservice_entries"),
            id: "examination_tracked_service_template_set",
            accessorKey: "examination_tracked_service_template_set",
            customColumnFilterId: "tracked_service_template__name",
            customSortingKey: "examination_tracked_service_template_set",
            sortDescFirst: false,
            accessorFn: (row: any) => {
              const examination_tracked_service_template_set: any = [];
              if (row.examination_tracked_service_template_set) {
                for (const key in row.examination_tracked_service_template_set) {
                  if (
                    row.examination_tracked_service_template_set[key]
                      .tracked_service_template.name
                  ) {
                    examination_tracked_service_template_set.push(
                      row.examination_tracked_service_template_set[key]
                        .tracked_service_template.name
                    );
                  }
                }
              }
              return examination_tracked_service_template_set.join(", ");
            },
          },
          {
            header: t("is_sensitive"),
            id: "is_sensitive",
            accessorKey: "is_sensitive",
            enableForCreate: true,
            filterVariant: "checkbox",

            selectOptions: [
              { value: true, label: t("yes") },
              { value: false, label: t("no") },
            ],
            accessorFn: (row: any) => {
              if (row.is_sensitive) {
                return t("yes");
              } else {
                return t("no");
              }
            },
          },
          {
            header: t("is_visible_in_preventive_care"),
            id: "visible_in_preventive_care_registry",
            accessorKey: "visible_in_preventive_care_registry",
            enableForCreate: true,
            selectOptions: [
              { value: true, label: t("yes") },
              { value: false, label: t("no") },
            ],
            accessorFn: (row: any) => {
              if (row.visible_in_preventive_care_registry) {
                return t("yes");
              } else {
                return t("no");
              }
            },
            filterVariant: "checkbox",
          },
          {
            header: t("is_visible_to_customers"),
            id: "visible_to_subjects",
            accessorKey: "visible_to_subjects",
            enableForCreate: true,
            selectOptions: [
              { value: true, label: t("yes") },
              { value: false, label: t("no") },
            ],
            accessorFn: (row: any) => {
              if (row.visible_to_subjects) {
                return t("yes");
              } else {
                return t("no");
              }
            },
            filterVariant: "checkbox",
          },
          {
            header: t("refresh_interval_in_days"),
            id: "interval_in_days",
            accessorKey: "interval_in_days",
            required: true,
            errorMessage: t("field_required"),
            enableForCreate: true,
          },
          {
            header: t("created_on"),
            accessorKey: "created_on",
            id: "created_on",
            sortingFn: "datetime",
            filterVariant: "date",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("created_by"),
            accessorKey: "created_by",
            customColumnFilterId: "created_by_name",

            id: "created_by",
            Cell: ({ cell }: any) => {
              return <span>{cell.getValue()?.name}</span>;
            },
          },
          {
            header: t("modified_on"),
            accessorKey: "modified_on",
            id: "modified_on",
            sortingFn: "datetime",
            filterVariant: "date",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("modified_by"),
            accessorKey: "modified_by",
            customColumnFilterId: "modified_by_name",

            id: "modified_by",
            Cell: ({ cell }: any) => {
              return <span>{cell.getValue()?.name}</span>;
            },
          },
        ]}
      />
      {errorModal}
      <YouTubePlayer
        url={youTubePlayerUrl}
        isOpen={youTubePlayerOpen}
        onClose={() => setYouTubePlayerOpen(false)}
      />
    </RestrictedPage>
  );
}
